import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./game.css";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import { selectAdminToken, selectSessionId, selectOrganizationId } from "../../admin/sessionSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { splitAnswers, isAcceptedAnswer } from "../../functions/answerMatching";

const BACKEND_URL = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const USER_DATA_KEY = "userData";

function parseMmSsToSeconds(t) {
  const m = /^(\d+):(\d{1,2})$/.exec(String(t ?? "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
}

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function ensureArray(val) {
  return Array.isArray(val) ? val : [];
}

/** Normalize an admin-saved category/year/question tree defensively — fields
 * may be missing on older/partial saves, so every access here has a fallback. */
function normalizeCategories(raw) {
  return ensureArray(raw)
    .slice(0, 6)
    .map((c, ci) => ({
      id: c?.id || `cat-${ci}`,
      name: String(c?.name ?? "").trim() || `Category ${ci + 1}`,
      color: c?.color || "#667eea",
      icon: c?.icon || "",
      desktopBg: c?.desktopBg || "",
      mobileBg: c?.mobileBg || "",
      titleImage: c?.titleImage || "",
      years: ensureArray(c?.years)
        .slice(0, 4)
        .map((y, yi) => ({
          id: y?.id || `year-${ci}-${yi}`,
          year: String(y?.year ?? "").trim(),
          questions: ensureArray(y?.questions)
            .slice(0, 5)
            .map((q, qi) => ({
              id: q?.id || `q-${ci}-${yi}-${qi}`,
              type: ["typetext", "truefalse"].includes(q?.type) ? q.type : "radioselect",
              question: String(q?.question ?? ""),
              image: q?.image || "",
              op1: String(q?.op1 ?? ""),
              op2: String(q?.op2 ?? ""),
              op3: String(q?.op3 ?? ""),
              answer: String(q?.answer ?? ""),
              blurAnswer: Boolean(q?.blurAnswer),
            })),
        })),
    }));
}

export default function GamePage() {
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId) || null;
  const organizationId = useSelector(selectOrganizationId) || null;

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
    } catch {
      return {};
    }
  }, []);
  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || null;

  // External report id (game-server). Created once at login via fetchReport;
  // the game page must only UPDATE that row — never add a second report.
  const readStoredReportId = () => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
      const rid = raw.reportId || raw.report_id || null;
      return rid != null && String(rid).trim() !== "" ? String(rid).trim() : null;
    } catch {
      return null;
    }
  };
  const [reportId, setReportId] = useState(
    () =>
      storedUserData?.reportId ||
      storedUserData?.report_id ||
      readStoredReportId() ||
      null
  );
  const reportIdRef = useRef(reportId);
  useEffect(() => {
    reportIdRef.current = reportId;
  }, [reportId]);

  const persistReportId = useCallback(
    (rid) => {
      if (!rid) return;
      const normalized = String(rid).trim();
      if (!normalized) return;
      reportIdRef.current = normalized;
      setReportId(normalized);
      try {
        const prev = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
        sessionStorage.setItem(
          USER_DATA_KEY,
          JSON.stringify({ ...prev, ...storedUserData, reportId: normalized })
        );
      } catch (_) {
        /* ignore */
      }
    },
    [storedUserData]
  );

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 992px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 992px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);


  const categories = useMemo(() => normalizeCategories(themeData?.categories), [themeData]);
  const pointsPerCorrect = Number(themeData?.points ?? 10) || 10;
  const yearIconUrl = themeData?.yearIcon ? getDvSourceImageUrl(themeData.yearIcon) : "";
  const questionBgUrl = themeData?.questionBg ? getDvSourceImageUrl(themeData.questionBg) : "";

  const themeVars = useMemo(
    () => ({
      "--theme-button-color": themeData?.button_color ?? "#667eea",
      "--theme-button-text-color": themeData?.button_Textcolor ?? themeData?.button_text_color ?? "#ffffff",
      "--theme-text-color": themeData?.textcolor ?? themeData?.text_color ?? "#1f2937",
    }),
    [themeData]
  );

  // Theme-level background (set at theme creation) — used for the category-select screen.
  const themeBackgroundUrl = useMemo(() => {
    if (!themeData) return "";
    const key = isMobileViewport
      ? (themeData.background_mob || themeData.mobile_view_image)
      : (themeData.background_desk || themeData.desktop_view_image);
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, isMobileViewport]);

  // ── View state: "categories" | "category" (the category/board page). A question
  // is an overlay on top of "category" (activeYearId set), not a separate page. ──
  const [view, setView] = useState("categories");
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeYearId, setActiveYearId] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [categoryCompleteMsg, setCategoryCompleteMsg] = useState(null); // { categoryName } | null
  const [gameCompletePopup, setGameCompletePopup] = useState(false);

  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [answerLog, setAnswerLog] = useState([]); // [{ questionId, isCorrect }]
  const [inlineNotice, setInlineNotice] = useState("");
  const [showMissingContentPopup, setShowMissingContentPopup] = useState(false);
  const [statLoaded, setStatLoaded] = useState(false);

  // Incomplete admin setup (category with no years, or year with no questions) —
  // keep the player locked out until content is added.
  const hasMissingContent = useMemo(
    () =>
      categories.length > 0 &&
      categories.some(
        (c) => c.years.length === 0 || c.years.some((y) => y.questions.length === 0)
      ),
    [categories]
  );

  useEffect(() => {
    if (!hasMissingContent) return;
    setShowMissingContentPopup(true);
    // Close any open question so the player cannot answer while content is incomplete.
    setActiveYearId(null);
  }, [hasMissingContent]);

  // ── Score/progress persistence (`stat` table — one row per userid+organizationId+sessionId) ──
  const scoreRef = useRef(score);
  useEffect(() => { scoreRef.current = score; }, [score]);
  const elapsedRef = useRef(elapsed);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);
  const answerLogRef = useRef(answerLog);
  useEffect(() => { answerLogRef.current = answerLog; }, [answerLog]);

  /**
   * Ensure we have a reportId: memory → sessionStorage → /fetchReport.
   * Passing role is safe: backend only creates when `stat.reportid` is empty.
   */
  const resolveExistingReportId = useCallback(async () => {
    const fromMem = reportIdRef.current || readStoredReportId();
    if (fromMem) {
      persistReportId(fromMem);
      return fromMem;
    }
    if (!adminToken || !userId) return null;
    const role = storedUserData?.role || storedUserData?.source;
    try {
      const res = await fetch(`${BACKEND_URL}/fetchReport`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId,
          role: role || undefined,
          token: storedUserData?.token || undefined,
          gameId: storedUserData?.gameId || undefined,
          name: storedUserData?.name || undefined,
          fullname: storedUserData?.name || undefined,
          email: storedUserData?.email || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      const rid =
        data?.report?.reportId != null && String(data.report.reportId).trim() !== ""
          ? String(data.report.reportId).trim()
          : null;
      if (rid) persistReportId(rid);
      return rid;
    } catch {
      return null;
    }
  }, [adminToken, userId, storedUserData, persistReportId]);

  // Load reportId as soon as game opens so every save can pass it.
  useEffect(() => {
    if (!adminToken || !userId) return undefined;
    let cancelled = false;
    (async () => {
      const rid = await resolveExistingReportId();
      if (!cancelled && rid) persistReportId(rid);
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, userId, resolveExistingReportId, persistReportId]);

  /**
   * Pass the existing reportId when we have one (sendReport then UPDATEs);
   * when we don't, sendReport creates a new report and returns its id —
   * mirrors states' pushExternalReport, which never requires an existing id
   * up front (see states GamePage.jsx `pushExternalReport`).
   */
  const pushExternalReport = useCallback(
    async (currentScore, currentTime) => {
      const role = storedUserData?.role || storedUserData?.source;
      if (role === "demobypass" || role === "DEMO") return reportIdRef.current;
      if (!userId || !sessionId || !organizationId) return reportIdRef.current;

      const existingId = await resolveExistingReportId();

      try {
        let name = typeof storedUserData?.name === "string" ? storedUserData.name.trim() : "";
        if (!name || /^null(\s+null)*$/i.test(name)) {
          name = storedUserData?.email || "Guest";
        }

        const response = await sendReport({
          sessionId,
          organizationId,
          userId,
          role,
          token: storedUserData?.token,
          gameId: storedUserData?.gameId,
          name,
          points: currentScore,
          time: formatTime(currentTime),
          reportId: existingId || undefined,
        });

        const newReportId = extractReportIdFromResponse(response) || existingId || null;

        if (newReportId) {
          persistReportId(newReportId);

          if (adminToken) {
            try {
              await fetch(`${BACKEND_URL}/welcomeStageLinkReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  organizationId,
                  sessionId,
                  userId,
                  reportId: newReportId,
                }),
              });
            } catch (linkErr) {
              console.error("Failed to link reportId:", linkErr);
            }
          }
        } else {
          console.warn("pushExternalReport: could not resolve reportId");
        }

        return newReportId;
      } catch (error) {
        console.error("Failed to send external report:", error);
        return existingId;
      }
    },
    [
      adminToken,
      userId,
      sessionId,
      organizationId,
      storedUserData,
      resolveExistingReportId,
      persistReportId,
    ]
  );

  const saveStat = useCallback(() => {
    if (!adminToken || !userId) return Promise.resolve();
    const points = scoreRef.current;
    const elapsedSec = elapsedRef.current;
    return (async () => {
      try {
        const rid =
          (await resolveExistingReportId()) ||
          reportIdRef.current ||
          readStoredReportId() ||
          undefined;
        await fetch(`${BACKEND_URL}/saveGameStat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            points,
            time: formatTime(elapsedSec),
            questions: answerLogRef.current,
            questionCount: answerLogRef.current.length,
            email: storedUserData?.email || undefined,
            fullname: storedUserData?.name || undefined,
            userType: storedUserData?.source || storedUserData?.role || undefined,
            reportId: rid,
          }),
        });
        await pushExternalReport(points, elapsedSec);
      } catch {
        /* best-effort */
      }
    })();
  }, [adminToken, userId, storedUserData, pushExternalReport, resolveExistingReportId]);

  // One-time hydration once the theme (and its categories, for stale-id filtering) is ready.
  const statHydratedRef = useRef(false);
  useEffect(() => {
    if (statHydratedRef.current || !themeData) return;
    if (!adminToken || !userId) {
      statHydratedRef.current = true;
      setStatLoaded(true);
      return undefined;
    }
    statHydratedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/getGameStat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ userId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !data?.success || !data.stat) return;

        const knownIds = new Set(categories.flatMap((c) => c.years.flatMap((y) => y.questions.map((q) => q.id))));
        const savedQuestions = Array.isArray(data.stat.questions) ? data.stat.questions : [];
        const filtered = savedQuestions.filter((a) => a && knownIds.has(a.questionId));

        setAnswerLog(filtered);
        setScore(
          filtered.length === savedQuestions.length
            ? Number(data.stat.points) || 0
            : filtered.filter((a) => a.isCorrect).length * pointsPerCorrect
        );
        setElapsed(parseMmSsToSeconds(data.stat.time));
      } catch {
        /* no saved progress reachable — start fresh */
      } finally {
        if (!cancelled) setStatLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [themeData, adminToken, userId, categories, pointsPerCorrect]);

  // Save after every newly-answered question (score + answerLog change together).
  useEffect(() => {
    if (!statLoaded) return;
    saveStat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerLog, statLoaded]);

  // Periodic autosave so elapsed time isn't lost between answers.
  useEffect(() => {
    if (!statLoaded) return undefined;
    const id = setInterval(saveStat, 20000);
    return () => clearInterval(id);
  }, [statLoaded, saveStat]);

  // Best-effort save on tab close / navigation away.
  useEffect(() => {
    window.addEventListener("beforeunload", saveStat);
    window.addEventListener("pagehide", saveStat);
    return () => {
      window.removeEventListener("beforeunload", saveStat);
      window.removeEventListener("pagehide", saveStat);
      saveStat();
    };
  }, [saveStat]);

  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [textAnswer, setTextAnswer] = useState("");
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [feedback, setFeedback] = useState(null); // { isCorrect, correctAnswer }
  const [canProceed, setCanProceed] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");
  const [confettiActive, setConfettiActive] = useState(false);

  const resetQuestionUi = useCallback(() => {
    setSelectedAnswer("");
    setTextAnswer("");
    setAnswerSubmitted(false);
    setFeedback(null);
    setCanProceed(false);
    setValidationMsg("");
    setConfettiActive(false);
  }, []);

  // Elapsed timer
  useEffect(() => {
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const answeredIds = useMemo(() => new Set(answerLog.map((a) => a.questionId)), [answerLog]);

  const isYearCompleted = useCallback(
    (year) => year.questions.length > 0 && year.questions.every((q) => answeredIds.has(q.id)),
    [answeredIds]
  );
  const isCategoryCompleted = useCallback(
    (cat) => cat.years.length > 0 && cat.years.every((y) => isYearCompleted(y)),
    [isYearCompleted]
  );

  const activeCategory = categories.find((c) => c.id === activeCategoryId) || null;

  const visibleYears = activeCategory?.years || [];

  const activeYear = activeCategory?.years.find((y) => y.id === activeYearId) || null;
  const currentQuestion = activeYear?.questions[questionIndex] || null;
  const questionOpen = Boolean(!hasMissingContent && activeYear && currentQuestion);

  const allCategoriesCompleted = categories.length > 0 && categories.every((c) => isCategoryCompleted(c));

  // All categories done: stay on the main page, show a congrats popup, then
  // head to the leaderboard — no separate "game complete" screen.
  useEffect(() => {
    if (allCategoriesCompleted && !gameCompletePopup) {
      setGameCompletePopup(true);
      setConfettiActive(true);
    }
  }, [allCategoriesCompleted, gameCompletePopup]);

  // Wait for the final save to actually land (not just a fixed timeout) before leaving for
  // the leaderboard — otherwise a slow save can lose the race and the leaderboard shows the
  // previous points/time instead of the final ones ("gap in time").
  useEffect(() => {
    if (!gameCompletePopup) return undefined;
    let cancelled = false;
    const minDisplay = new Promise((resolve) => setTimeout(resolve, 2600));
    Promise.all([saveStat(), minDisplay]).then(() => {
      if (!cancelled) navigate("/leaderboard");
    });
    return () => {
      cancelled = true;
    };
  }, [gameCompletePopup, navigate, saveStat]);

  // Category-complete popup auto-dismisses back to the main menu.
  useEffect(() => {
    if (!categoryCompleteMsg) return undefined;
    const t = setTimeout(() => {
      setCategoryCompleteMsg(null);
      resetQuestionUi();
      setActiveCategoryId(null);
      setActiveYearId(null);
      setView("categories");
    }, 2600);
    return () => clearTimeout(t);
  }, [categoryCompleteMsg, resetQuestionUi]);

  // The active category's own desktop/mobile background (picked per current viewport).
  const boardBgUrl = useMemo(() => {
    if (!activeCategory) return "";
    const key = isMobileViewport
      ? (activeCategory.mobileBg || activeCategory.desktopBg)
      : (activeCategory.desktopBg || activeCategory.mobileBg);
    return key ? getDvSourceImageUrl(key) : "";
  }, [activeCategory, isMobileViewport]);

  // Apply the active background directly to the document body/html — not just the inner
  // page div — so it truly covers the whole page (behind the outer header, no edge gaps,
  // no fallback color flashing through) exactly like the rest of the app does for themes.
  const activeBackgroundUrl = view === "category" && boardBgUrl ? boardBgUrl : themeBackgroundUrl;
  useEffect(() => {
    if (!activeBackgroundUrl) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const bg = `center / cover no-repeat url("${activeBackgroundUrl}")`;
    root.style.background = bg;
    body.style.background = bg;
    root.style.minHeight = "100%";
    body.style.minHeight = "100vh";
    return () => {
      root.style.removeProperty("background");
      root.style.removeProperty("min-height");
      body.style.removeProperty("background");
      body.style.removeProperty("min-height");
    };
  }, [activeBackgroundUrl]);

  // ── Navigation ──

  const openCategory = (cat) => {
    setInlineNotice("");
    if (hasMissingContent) {
      setShowMissingContentPopup(true);
      return;
    }
    if (isCategoryCompleted(cat)) {
      setInlineNotice(`You have already completed ${cat.name}.`);
      return;
    }
    if (cat.years.length === 0) {
      setShowMissingContentPopup(true);
      return;
    }
    setActiveCategoryId(cat.id);
    setView("category");
  };

  const openYear = (year) => {
    setInlineNotice("");
    if (hasMissingContent) {
      setShowMissingContentPopup(true);
      return;
    }
    if (isYearCompleted(year)) {
      setInlineNotice(`You have already completed ${year.year || "this milestone"}.`);
      return;
    }
    if (year.questions.length === 0) {
      setShowMissingContentPopup(true);
      return;
    }
    const nextIndex = year.questions.findIndex((q) => !answeredIds.has(q.id));
    resetQuestionUi();
    setActiveYearId(year.id);
    setQuestionIndex(nextIndex === -1 ? 0 : nextIndex);
  };

  const goToCategories = () => {
    resetQuestionUi();
    setActiveCategoryId(null);
    setActiveYearId(null);
    setView("categories");
  };

  // ── Answering ──

  const submitAnswer = () => {
    if (!currentQuestion) return;

    if (answerSubmitted) {
      if (!canProceed) return;
      resetQuestionUi();

      // More questions left in this year?
      const nextIdx = questionIndex + 1;
      if (nextIdx < activeYear.questions.length) {
        setQuestionIndex(nextIdx);
        return;
      }

      // Year finished — close the overlay, back to the board.
      setActiveYearId(null);
      if (isCategoryCompleted(activeCategory)) {
        const othersLeft = categories.some((c) => c.id !== activeCategory.id && !isCategoryCompleted(c));
        if (othersLeft) {
          setCategoryCompleteMsg({ categoryName: activeCategory.name });
        }
        setConfettiActive(true);
      }
      return;
    }

    let userAnswer = "";
    if (currentQuestion.type === "typetext") {
      userAnswer = textAnswer.trim();
      if (!userAnswer) {
        setValidationMsg("Please type your answer before submitting.");
        return;
      }
    } else {
      userAnswer = selectedAnswer;
      if (!userAnswer) {
        setValidationMsg("Please select an option before submitting.");
        return;
      }
    }
    setValidationMsg("");

    const isCorrect = isAcceptedAnswer(currentQuestion.answer, userAnswer);
    const alreadyAnswered = answeredIds.has(currentQuestion.id);

    if (!alreadyAnswered) {
      setAnswerLog((prev) => [...prev, { questionId: currentQuestion.id, isCorrect }]);
      if (isCorrect) setScore((prev) => prev + pointsPerCorrect);
    }

    setAnswerSubmitted(true);
    setFeedback({ isCorrect, correctAnswer: splitAnswers(currentQuestion.answer)[0] || currentQuestion.answer });
    setCanProceed(true);
    if (isCorrect) {
      setConfettiActive(true);
    } else if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(150);
    }
  };

  const radioOptions = currentQuestion
    ? currentQuestion.type === "truefalse"
      ? [currentQuestion.op1, currentQuestion.op2].filter(Boolean)
      : [currentQuestion.op1, currentQuestion.op2, currentQuestion.op3].filter(Boolean)
    : [];

  // Admin-configured per-question: blur the question image until the player
  // submits an answer, then it unblurs automatically.
  const shouldBlurImage = !!currentQuestion?.blurAnswer && !answerSubmitted;

  const adminRules = useMemo(() => {
    const val = themeData?.rules;
    if (!val) return null;
    const arr = ensureArray(val).map((v) => String(v).trim()).filter(Boolean);
    return arr.length ? arr : null;
  }, [themeData]);

  const defaultRules = [
    "Tap a category to begin",
    "Pick a year milestone, then answer its question(s)",
    `Earn ${pointsPerCorrect} points for each correct answer`,
    "Complete every category to finish the game",
  ];
  const rulesList = adminRules || defaultRules;

  if (!themeData) {
    return (
      <div className="game-loading" style={themeVars}>
        <p>Loading game...</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="game-loading" style={themeVars}>
        <p>No categories have been configured for this game yet.</p>
      </div>
    );
  }

  return (
    <div className="igame-page" style={themeVars}>
      {confettiActive && <ConfettiBurst onDone={() => setConfettiActive(false)} />}

      {/* ── Top bar (the outer page header already provides the site logo + Back) ── */}
      <div className={`igame-topbar${view === "categories" ? " is-categories-view" : ""}`}>
        {view === "category" && activeCategory && (
          <div className="igame-topbar-title">
            {activeCategory.titleImage ? (
              <img src={getDvSourceImageUrl(activeCategory.titleImage)} alt={activeCategory.name} className="igame-board-title-img" />
            ) : (
              <span className="igame-board-title-badge" style={{ background: activeCategory.color }}>{activeCategory.name}</span>
            )}
          </div>
        )}

        {/* On desktop this is fixed/floating independently (top-left); on mobile it
            becomes a normal flex item so it can share one row with Main Menu/Points/Rules. */}
        {view === "category" && (
          <div className="igame-time-badge">Time : {formatTime(elapsed)}</div>
        )}

        {view === "category" && (
          <button type="button" className="igame-pill-btn igame-mainmenu-btn" onClick={goToCategories}>Main Menu</button>
        )}

        <div className="igame-topbar-right">
          <span className="igame-pill-btn igame-points-badge">Points: {score}</span>
          <button type="button" className="igame-rules-btn" onClick={() => setShowRules(true)} aria-label="How to play">?</button>
        </div>
      </div>

      {inlineNotice && <div className="inline-notice" role="status">{inlineNotice}</div>}

      {/* ── Category select ── */}
      {view === "categories" && (
        <div className="igame-categories-view">
          <div className="igame-category-grid">
            {categories.map((cat) => {
              const completed = isCategoryCompleted(cat);
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`igame-category-tile${completed ? " is-completed" : ""}`}
                  style={{ "--tile-color": cat.color }}
                  onClick={() => openCategory(cat)}
                  aria-disabled={hasMissingContent}
                >
                  <span className="igame-tile-icon-wrap">
                    {cat.icon ? (
                      <img src={getDvSourceImageUrl(cat.icon)} alt="" className="igame-tile-icon" />
                    ) : (
                      <span className="igame-tile-icon-fallback">{cat.name.charAt(0)}</span>
                    )}
                  </span>
                  {cat.titleImage ? (
                    <img src={getDvSourceImageUrl(cat.titleImage)} alt={cat.name} className="igame-tile-title-img" />
                  ) : (
                    <span className="igame-tile-title-text" style={{ background: cat.color }}>{cat.name}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Category page: year board (+ question overlay) ── */}
      {view === "category" && activeCategory && (
        <div className="igame-board-view">
          <div className="igame-year-row">
            {visibleYears.map((year) => {
              const completed = isYearCompleted(year);
              return (
                <button
                  key={year.id}
                  type="button"
                  className={`igame-year-marker${completed ? " is-completed" : ""}`}
                  onClick={() => openYear(year)}
                  aria-disabled={hasMissingContent}
                >
                  {completed && <span className="igame-tile-check"><CheckIcon /></span>}
                  {yearIconUrl && <img src={yearIconUrl} alt="" className="igame-year-icon" />}
                  <span className="igame-year-label">{year.year || "?"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Question overlay (answering is compulsory — no backdrop-click dismiss) ── */}
      {questionOpen && (
        <div className="igame-question-overlay">
          <div
            className="igame-question-view"
            style={questionBgUrl ? { backgroundImage: toCssUrlValue(questionBgUrl) } : undefined}
          >
            <div className={`question-card${feedback && !feedback.isCorrect ? " is-wrong-shake" : ""}`}>
              <span className="qc-tape qc-tape-tl" />
              <span className="qc-tape qc-tape-tr" />
              <span className="qc-tape qc-tape-bl" />
              <span className="qc-tape qc-tape-br" />

              <div className="question-text-display">
                <p>{currentQuestion.question}</p>
              </div>

              {currentQuestion.image && (
                <div className={`question-image-display${shouldBlurImage ? " is-blurred" : ""}`}>
                  <img src={getDvSourceImageUrl(currentQuestion.image)} alt="Question" />
                </div>
              )}

              {(currentQuestion.type === "radioselect" || currentQuestion.type === "truefalse") && (
                <div className="options-display">
                  {radioOptions.map((opt) => {
                    const isCorrectOpt = answerSubmitted && isAcceptedAnswer(currentQuestion.answer, opt);
                    const isWrongOpt = answerSubmitted && selectedAnswer === opt && !feedback?.isCorrect;
                    return (
                      <label
                        key={opt}
                        className={[
                          "option-label",
                          selectedAnswer === opt && !answerSubmitted ? "is-selected" : "",
                          answerSubmitted ? "is-locked" : "",
                          isCorrectOpt ? "is-correct" : "",
                          isWrongOpt ? "is-wrong" : "",
                        ].filter(Boolean).join(" ")}
                      >
                        <input
                          type="radio"
                          name="answer"
                          value={opt}
                          checked={selectedAnswer === opt}
                          disabled={answerSubmitted}
                          onChange={(e) => setSelectedAnswer(e.target.value)}
                        />
                        <span className="opt-text">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {currentQuestion.type === "typetext" && (
                <div className="text-input-display">
                  <input
                    type="text"
                    placeholder="Type your answer here"
                    value={textAnswer}
                    disabled={answerSubmitted}
                    className={answerSubmitted ? (feedback?.isCorrect ? "is-correct" : "is-wrong") : ""}
                    onChange={(e) => {
                      setTextAnswer(e.target.value);
                      if (validationMsg) setValidationMsg("");
                    }}
                  />
                </div>
              )}

              {validationMsg && <p className="validation-msg" role="alert">{validationMsg}</p>}

              {feedback && !feedback.isCorrect && (
                <div className="answer-feedback">
                  <p className="feedback-correct-answer">
                    Correct answer is: <strong>{feedback.correctAnswer}</strong>
                  </p>
                </div>
              )}

              <div className="button-area">
                <button className="next-button" onClick={submitAnswer} disabled={answerSubmitted && !canProceed}>
                  {answerSubmitted ? "Next" : "Submit"}
                </button>
              </div>

              <div className="question-progress">
                Question {questionIndex + 1} of {activeYear.questions.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Category-complete popup (auto-dismisses back to Main Menu) ── */}
      {categoryCompleteMsg && (
        <div className="igame-category-complete-overlay">
          <div className="igame-category-complete-card">
            <h2>Completed!</h2>
            <p>You've successfully completed {categoryCompleteMsg.categoryName}!</p>
          </div>
        </div>
      )}

      {/* ── Rules popup ── */}
      {showRules && (
        <div className="igame-modal-overlay" onClick={() => setShowRules(false)}>
          <div
            className="igame-rules-panel"
            style={
              themeBackgroundUrl
                ? {
                    backgroundImage: `${toCssUrlValue(themeBackgroundUrl)}`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="igame-rules-header">
              <h2>How to play</h2>
              <button type="button" className="igame-rules-close" onClick={() => setShowRules(false)} aria-label="Close">&times;</button>
            </div>
            <ul className="rules-list">
              {rulesList.map((rule, i) => (
                <li key={i}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Missing-content popup — blocks play until admin adds questions ── */}
      {showMissingContentPopup && (
        <div className="igame-modal-overlay" role="alertdialog" aria-modal="true" aria-label="Missing questions">
          <div
            className="igame-rules-panel igame-missing-content-panel"
            style={
              themeBackgroundUrl
                ? {
                    backgroundImage: `${toCssUrlValue(themeBackgroundUrl)}`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            <p className="igame-missing-content-message">Please Add questions from admin</p>
            <button
              type="button"
              className="next-button"
              onClick={() => {
                // Dismissing must not unlock play — re-show when they try to open a question.
                setShowMissingContentPopup(false);
                if (hasMissingContent) {
                  resetQuestionUi();
                  setActiveYearId(null);
                  setActiveCategoryId(null);
                  setView("categories");
                }
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ── Game-complete popup (auto-redirects to the leaderboard) ── */}
      {gameCompletePopup && (
        <div className="igame-category-complete-overlay">
          <div className="igame-category-complete-card igame-game-complete-card">
            <h2>Congratulations!</h2>
            <p>You completed all categories!</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfettiBurst({ onDone }) {
  const canvasRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#E25569", "#FB9946", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6B9D", "#C77DFF"];
    const particles = [];

    [{ x: 0, y: canvas.height, dir: 1 }, { x: canvas.width, y: canvas.height, dir: -1 }].forEach((origin) => {
      for (let i = 0; i < 90; i += 1) {
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: origin.dir * (Math.random() * 8 + 3) + (Math.random() - 0.5) * 6,
          vy: -(Math.random() * 14 + 6),
          w: Math.random() * 9 + 4,
          h: Math.random() * 6 + 3,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * 360,
          spin: (Math.random() - 0.5) * 12,
          life: 1,
          gravity: 0.22,
        });
      }
    });

    let frameId;
    let ticks = 0;
    const maxTicks = 180;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        if (p.life <= 0) return;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;
        p.life -= 0.007;
        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      ticks += 1;
      if (ticks < maxTicks) {
        frameId = requestAnimationFrame(draw);
      } else {
        onDoneRef.current?.();
      }
    };

    frameId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />;
}
